import React, { createContext, useContext, useEffect, useState } from "react";
import { MutationKey } from "react-query";

// This type defines the mapping between the event types
// and their detail types. It is not instantiated directly.
type AppEventTypeMap = {
  logout: null;
  "account:create": {
    cell: string;
    plan_id: string;
    name: string;
  };
  "mutation:success": {
    key: MutationKey;
    variables: unknown;
    data: unknown;
    context: unknown;
  };
  "mutation:error": {
    key: MutationKey;
    variables: unknown;
    error: unknown;
    context: unknown;
  };
};

export type AppEventType = keyof AppEventTypeMap;
export type AppEventDetail<T extends AppEventType> = AppEventTypeMap[T];
export type AppEventHandler<T extends AppEventType> = (
  event: CustomEvent<AppEventDetail<T>>
) => void;
export type AppEvent<T extends AppEventType> = CustomEvent<AppEventDetail<T>>;

/**
 * AppEventTarget is a thin wrapper over the browser's EventTarget.
 * Its reason for being is to provide type safety by mapping the event
 * type to the correct detail type when dispatching or subscribing
 * to events.
 */
export class AppEventTarget {
  private target: EventTarget;

  constructor() {
    this.target = new EventTarget();
  }

  on<T extends AppEventType>(eventType: T, handler: AppEventHandler<T>) {
    this.target.addEventListener(eventType, handler);
  }

  off<T extends AppEventType>(eventType: T, handler: AppEventHandler<T>) {
    this.target.removeEventListener(eventType, handler);
  }

  dispatch = <T extends AppEventType>(
    eventType: T,
    detail?: AppEventDetail<T>
  ) => {
    this.target.dispatchEvent(new CustomEvent(eventType, { detail }));
  };
}

export type AppEventDispatchFn = AppEventTarget["dispatch"];

export const AppEventsContext = createContext<AppEventTarget>(
  new AppEventTarget()
);

export function AppEventsProvider({ children }) {
  const [target] = useState(() => new AppEventTarget());
  return (
    <AppEventsContext.Provider value={target}>
      {children}
    </AppEventsContext.Provider>
  );
}

/**
 * Returns the AppEventsTarget from context.
 * Most clients should use useAppEventListener
 * or useAppEventDispatch instead.
 */
export function useAppEventTarget() {
  return useContext(AppEventsContext);
}

export function useAppEventListener<T extends AppEventType>(
  event: T,
  handler: AppEventHandler<T>
) {
  const target = useAppEventTarget();
  useEffect(() => {
    target.on(event, handler);
    return () => target.off(event, handler);
  }, [target, event, handler]);
}

export function useAppEventDispatch(): AppEventDispatchFn {
  const target = useAppEventTarget();
  return target.dispatch;
}
